import { useState, useEffect, useRef } from 'react';
import { useRecaptchaV3 } from '../contexts/RecaptchaContext';

/**
 * Custom hook for comprehensive bot detection
 * Combines:
 * 1. Honeypot field detection (hidden field that bots fill but humans don't)
 * 2. Form timing detection (bots submit too fast)
 * 3. reCAPTCHA v3 background scoring
 * 4. reCAPTCHA v2 fallback for suspicious traffic
 *
 * @param {Object} options - Configuration options
 * @param {number} options.minimumTimeMs - Minimum time (ms) before submission is valid (default: 2000)
 * @param {number} options.v3ScoreThreshold - Minimum v3 score to avoid v2 challenge (default: 0.3)
 * @returns {Object} Bot detection state and methods
 */
const useBotDetection = (options = {}) => {
  const {
    minimumTimeMs = 2000 // Minimum 2 seconds to fill form
    // v3ScoreThreshold would be used for backend score verification
  } = options;

  const { executeV3 } = useRecaptchaV3();

  // State
  const [honeypotValue, setHoneypotValue] = useState('');
  const [showV2Challenge, setShowV2Challenge] = useState(false);
  const [v2Token, setV2Token] = useState(null);
  const [v3Token, setV3Token] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [botDetectionResult, setBotDetectionResult] = useState(null);

  // Refs
  const formLoadTime = useRef(Date.now());
  const hasRunV3 = useRef(false);

  // Reset load time when component mounts
  useEffect(() => {
    formLoadTime.current = Date.now();
  }, []);

  /**
   * Honeypot field props
   * Hidden field that should never be filled by real users
   */
  const honeypotFieldProps = {
    value: honeypotValue,
    onChange: (e) => setHoneypotValue(e.target.value),
    tabIndex: -1,
    autoComplete: 'off',
    style: {
      position: 'absolute',
      left: '-9999px',
      width: '1px',
      height: '1px',
      opacity: 0,
      pointerEvents: 'none'
    },
    'aria-hidden': 'true',
    name: 'website' // Common honeypot field name
  };

  /**
   * Check form timing - has enough time passed?
   */
  const checkFormTiming = () => {
    const timeSpent = Date.now() - formLoadTime.current;
    return {
      timeSpent,
      isSuspicious: timeSpent < minimumTimeMs,
      passed: timeSpent >= minimumTimeMs
    };
  };

  /**
   * Check honeypot field - is it filled?
   */
  const checkHoneypot = () => {
    const isTriggered = honeypotValue.length > 0;
    return {
      value: honeypotValue,
      isTriggered,
      passed: !isTriggered
    };
  };

  /**
   * Execute reCAPTCHA v3 and determine if v2 challenge is needed
   */
  const executeV3Check = async (action = 'form_submit') => {
    if (hasRunV3.current) {
      return v3Token;
    }

    try {
      const token = await executeV3(action);
      setV3Token(token);
      hasRunV3.current = true;

      // In production, you would verify this token on backend and get actual score
      // For now, we'll use a simulated approach
      // Note: v3 tokens must be verified server-side to get actual score

      // Push to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'recaptcha_v3_executed',
        recaptchaV3Token: token ? 'present' : 'missing',
        recaptchaAction: action
      });

      return token;
    } catch (error) {
      console.error('reCAPTCHA v3 execution failed:', error);
      return null;
    }
  };

  /**
   * Handle reCAPTCHA v2 completion
   */
  const handleV2Change = (token) => {
    setV2Token(token);
    if (token) {
      setIsVerified(true);
    }
  };

  /**
   * Main validation function - call this before form submission
   * Returns comprehensive bot detection results
   */
  const validateSubmission = async (action = 'form_submit') => {
    const honeypotResult = checkHoneypot();
    const timingResult = checkFormTiming();

    // Immediate fail if honeypot is triggered
    if (honeypotResult.isTriggered) {
      const result = {
        isBot: true,
        passed: false,
        reason: 'honeypot_triggered',
        honeypot: honeypotResult,
        timing: timingResult,
        requiresV2: false,
        canSubmit: false
      };
      setBotDetectionResult(result);

      // Push to GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'bot_detected',
        botDetectionMethod: 'honeypot',
        formTimingMs: timingResult.timeSpent
      });

      return result;
    }

    // Execute v3 check
    const v3TokenResult = await executeV3Check(action);

    // Determine if v2 challenge is needed based on timing and v3
    // If form filled too fast OR v3 score unavailable, show v2
    const needsV2 = timingResult.isSuspicious || !v3TokenResult;

    if (needsV2 && !showV2Challenge && !v2Token) {
      setShowV2Challenge(true);
      const result = {
        isBot: false,
        passed: false,
        reason: 'verification_required',
        honeypot: honeypotResult,
        timing: timingResult,
        requiresV2: true,
        canSubmit: false,
        message: 'Please complete the verification to continue.'
      };
      setBotDetectionResult(result);
      return result;
    }

    // Check if v2 is required but not completed
    if (needsV2 && !v2Token) {
      const result = {
        isBot: false,
        passed: false,
        reason: 'v2_verification_incomplete',
        honeypot: honeypotResult,
        timing: timingResult,
        requiresV2: true,
        canSubmit: false,
        message: 'Please complete the reCAPTCHA verification.'
      };
      setBotDetectionResult(result);
      return result;
    }

    // All checks passed!
    const result = {
      isBot: false,
      passed: true,
      reason: 'all_checks_passed',
      honeypot: honeypotResult,
      timing: timingResult,
      requiresV2: needsV2,
      v2Completed: !!v2Token,
      v3Token: v3TokenResult,
      v2Token: v2Token,
      canSubmit: true
    };
    setBotDetectionResult(result);
    setIsVerified(true);

    // Push success to GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_validation_passed',
      formTimingMs: timingResult.timeSpent,
      recaptchaV2Required: needsV2,
      recaptchaV2Passed: !!v2Token
    });

    return result;
  };

  /**
   * Reset all bot detection state
   */
  const reset = () => {
    setHoneypotValue('');
    setShowV2Challenge(false);
    setV2Token(null);
    setV3Token(null);
    setIsVerified(false);
    setBotDetectionResult(null);
    formLoadTime.current = Date.now();
    hasRunV3.current = false;
  };

  return {
    // State
    honeypotValue,
    showV2Challenge,
    v2Token,
    v3Token,
    isVerified,
    botDetectionResult,

    // Methods
    validateSubmission,
    handleV2Change,
    reset,
    checkHoneypot,
    checkFormTiming,

    // Props for components
    honeypotFieldProps
  };
};

export default useBotDetection;
